import { GoogleGenerativeAI } from "@google/generative-ai";
import faqData from "../data/faq.json";

// Initialize Gemini API with fallback and validation
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

// Remove any hardcoded API key that might be causing conflicts

if (!API_KEY) {
  console.error(
    "Gemini API key is not set. Please set REACT_APP_GEMINI_API_KEY in your environment variables."
  );
}

// Log the first few characters of the API key for debugging (don't log the full key)
if (API_KEY) {
  console.log(`API key loaded (first 4 chars): ${API_KEY.substring(0, 4)}...`);
} else {
  console.log("No API key found");
}

let genAI;
try {
  genAI = API_KEY ? new GoogleGenerativeAI(API_KEY) : null;
} catch (error) {
  console.error("Failed to initialize Gemini API:", error);
}

// Maintain a list of used suggestions to avoid repetition
let usedSuggestions = [];

// Function to extract suggested questions from FAQ data
export function getSuggestedQuestions(count = 5) {
  try {
    if (!faqData || !faqData.faqs || !Array.isArray(faqData.faqs)) {
      console.error("Invalid FAQ data format");
      return getDefaultSuggestions();
    }

    // Create an array of all questions from all categories
    const allQuestions = [];
    faqData.faqs.forEach((category) => {
      if (category.questions && Array.isArray(category.questions)) {
        category.questions.forEach((faq) => {
          if (faq.question) {
            allQuestions.push(faq.question);
          }
        });
      }
    });

    // If no questions found, return defaults
    if (allQuestions.length === 0) {
      return getDefaultSuggestions();
    }

    // If we don't have enough total questions, just return what we have
    if (allQuestions.length <= count) {
      // Shuffle and return all available questions
      const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
      return shuffled;
    }

    // Filter out previously used suggestions
    let availableQuestions = allQuestions.filter(
      (q) => !usedSuggestions.includes(q)
    );

    // If we've used all questions, reset the used list
    if (availableQuestions.length < count) {
      usedSuggestions = [];
      availableQuestions = allQuestions;
    }

    // Shuffle array to randomize selection
    const shuffled = [...availableQuestions].sort(() => 0.5 - Math.random());

    // Get the suggestions
    const selectedSuggestions = shuffled.slice(0, count);

    // Add to used list
    usedSuggestions.push(...selectedSuggestions);

    return selectedSuggestions;
  } catch (error) {
    console.error("Error getting suggested questions:", error);
    return getDefaultSuggestions();
  }
}
// Fallback default suggestions if something goes wrong
function getDefaultSuggestions() {
  return [
    "What is the Financial Literacy Program?",
    "How can I register for the program?",
    "Are there any eligibility criteria?",
    "What are the benefits of the course?",
    "How do I take the final exam?",
  ];
}

// Function to get embeddings from Gemini
async function getEmbedding(text) {
  try {
    if (!text || typeof text !== "string") {
      throw new Error("Invalid input text");
    }

    if (!API_KEY) {
      throw new Error("API key is not configured");
    }

    if (!genAI) {
      throw new Error("Gemini API not initialized");
    }

    const model = genAI.getGenerativeModel({ model: "embedding-001" });
    const result = await model.embedContent(text);

    if (!result || !result.embedding) {
      throw new Error("Invalid embedding result");
    }

    // Handle different possible embedding formats
    let embedding;
    if (Array.isArray(result.embedding)) {
      embedding = result.embedding;
    } else if (typeof result.embedding === "object") {
      embedding = Object.values(result.embedding);
    } else if (typeof result.embedding === "string") {
      try {
        embedding = JSON.parse(result.embedding);
      } catch (e) {
        throw new Error("Invalid embedding string format");
      }
    } else {
      throw new Error("Unsupported embedding format");
    }

    // Validate the embedding array
    if (!Array.isArray(embedding) || embedding.length === 0) {
      throw new Error("Empty or invalid embedding array");
    }

    // Convert all values to numbers
    embedding = embedding.map((val) => {
      const num = Number(val);
      if (isNaN(num)) {
        throw new Error("Non-numeric value in embedding");
      }
      return num;
    });

    return embedding;
  } catch (error) {
    console.error("Error getting embedding:", error);
    return null; // Return null instead of throwing to handle gracefully
  }
}

// Function to calculate cosine similarity
function cosineSimilarity(vecA, vecB) {
  try {
    if (!Array.isArray(vecA) || !Array.isArray(vecB)) {
      throw new Error("Vectors must be arrays");
    }

    if (vecA.length !== vecB.length) {
      throw new Error("Vectors must have the same length");
    }

    if (
      !vecA.every((num) => typeof num === "number") ||
      !vecB.every((num) => typeof num === "number")
    ) {
      throw new Error("Vectors must contain only numbers");
    }

    const dotProduct = vecA.reduce((sum, a, i) => sum + a * vecB[i], 0);
    const magnitudeA = Math.sqrt(vecA.reduce((sum, a) => sum + a * a, 0));
    const magnitudeB = Math.sqrt(vecB.reduce((sum, b) => sum + b * b, 0));

    if (magnitudeA === 0 || magnitudeB === 0) {
      return 0;
    }

    return dotProduct / (magnitudeA * magnitudeB);
  } catch (error) {
    console.error("Error calculating similarity:", error);
    return 0;
  }
}

// Get a direct answer by question without going through the searching process again
export function getDirectAnswerByQuestion(question) {
  try {
    if (!question || typeof question !== "string") {
      throw new Error("Invalid question");
    }

    // Search through the FAQ data for exact match
    for (const category of faqData.faqs) {
      for (const faq of category.questions) {
        if (
          faq.question.toLowerCase().trim() === question.toLowerCase().trim()
        ) {
          return {
            question: faq.question,
            answer: faq.answer,
            category: category.category,
            score: 1.0,
          };
        }
      }
    }

    // If no exact match, return null and let the normal search process handle it
    return null;
  } catch (error) {
    console.error("Error getting direct answer by question:", error);
    return null;
  }
}

// Enhanced Function to find the best matching FAQ using better text matching
export async function findBestMatchingFAQ(query) {
  try {
    if (!query || typeof query !== "string") {
      throw new Error("Invalid query");
    }

    // Get dynamic suggested questions
    const suggestedQuestions = getSuggestedQuestions(100);

    // Check for direct matches with suggested questions first
    for (const suggestedQ of suggestedQuestions) {
      if (query.toLowerCase().trim() === suggestedQ.toLowerCase().trim()) {
        // Find the matching FAQ from our data
        for (const category of faqData.faqs) {
          for (const faq of category.questions) {
            if (
              faq.question.toLowerCase().trim() ===
              suggestedQ.toLowerCase().trim()
            ) {
              return {
                question: faq.question,
                answer: faq.answer,
                category: category.category,
                score: 1.0,
              };
            }
          }
        }
      }
    }

    // Convert query to lowercase for case-insensitive matching
    const normalizedQuery = query.toLowerCase().trim();

    // Pre-process query - remove common filler words
    const stopWords = [
      "a",
      "an",
      "the",
      "and",
      "or",
      "but",
      "is",
      "are",
      "was",
      "were",
      "be",
      "been",
      "being",
    ];
    const queryWords = normalizedQuery
      .split(/\s+/)
      .filter((word) => !stopWords.includes(word));

    let bestMatch = null;
    let highestScore = 0;

    // First try direct keyword matching
    for (const category of faqData.faqs) {
      for (const faq of category.questions) {
        // Convert FAQ question to lowercase
        const normalizedQuestion = faq.question.toLowerCase().trim();

        // Calculate scoring
        let score = 0;

        // Check for exact matches (prioritize these)
        if (normalizedQuery === normalizedQuestion) {
          score = 1.0;
        } else {
          // Check for keyword presence - better partial matching
          const questionWords = normalizedQuestion
            .split(/\s+/)
            .filter((word) => !stopWords.includes(word));

          // Count matching words with weighted scoring
          let matchCount = 0;
          let exactMatchCount = 0;

          for (const queryWord of queryWords) {
            // Check for exact word matches
            if (questionWords.includes(queryWord)) {
              exactMatchCount++;
              matchCount++;
            }
            // Check for substring matches
            else if (
              questionWords.some(
                (qWord) =>
                  qWord.includes(queryWord) || queryWord.includes(qWord)
              )
            ) {
              matchCount += 0.5; // Partial match gets less weight
            }
          }

          // Calculate weighted score
          if (queryWords.length > 0) {
            const exactMatchScore = exactMatchCount / queryWords.length;
            const partialMatchScore = matchCount / queryWords.length;

            // Combine scores, favoring exact matches
            score = exactMatchScore * 0.7 + partialMatchScore * 0.3;
          }

          // Bonus for matching category keywords
          if (normalizedQuery.includes(category.category.toLowerCase())) {
            score += 0.1;
          }

          // Additional scoring for key terms
          if (
            normalizedQuery.includes("exam") &&
            normalizedQuestion.includes("exam")
          ) {
            score += 0.2;
          }
          if (
            normalizedQuery.includes("enroll") &&
            (normalizedQuestion.includes("enroll") ||
              normalizedQuestion.includes("register"))
          ) {
            score += 0.2;
          }
          if (
            normalizedQuery.includes("program") &&
            normalizedQuestion.includes("program")
          ) {
            score += 0.15;
          }
        }

        // Update best match if this is more similar
        if (score > highestScore) {
          highestScore = score;
          bestMatch = {
            question: faq.question,
            answer: faq.answer,
            category: category.category,
            score,
          };
        }
      }
    }

    // Lower the threshold to catch more matches
    return highestScore > 0.3 ? bestMatch : null;
  } catch (error) {
    console.error("Error finding matching FAQ:", error);
    return null; // Return null instead of throwing
  }
}

// Function to get answer from Gemini if no FAQ match is found
export async function getGeminiAnswer(query, attemptCount = 0) {
  try {
    if (!query || typeof query !== "string") {
      throw new Error("Invalid query");
    }

    // Max retries
    const maxRetries = 2;

    // Enhanced prompt with more context about the FLP program
    const enhancedPrompt = `
    You are an AI assistant for the BharatFLP (Financial Literacy Program). Please answer the following question 
    about financial literacy education for students in grades 7-12:
    
    ${query}
    
    Keep your answer concise, accurate, and helpful. If the question is not related to financial literacy or education,
    politely redirect to relevant topics about financial education.
    `;

    // Use the existing Gemini API endpoint with the correct API key
    const response = await fetch(
      "https://generativelanguage.googleapis.com/v1/models/gemini-1.5-pro:generateContent?key=" +
        process.env.REACT_APP_GEMINI_API_KEY,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: enhancedPrompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          },
          safetySettings: [
            {
              category: "HARM_CATEGORY_HARASSMENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE",
            },
            {
              category: "HARM_CATEGORY_HATE_SPEECH",
              threshold: "BLOCK_MEDIUM_AND_ABOVE",
            },
            {
              category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE",
            },
            {
              category: "HARM_CATEGORY_DANGEROUS_CONTENT",
              threshold: "BLOCK_MEDIUM_AND_ABOVE",
            },
          ],
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error(
        `API request failed with status ${response.status}:`,
        errorData
      );

      // Network error handling with retry logic
      if (attemptCount < maxRetries) {
        console.log(
          `Retrying API request (${attemptCount + 1}/${maxRetries})...`
        );
        return getGeminiAnswer(query, attemptCount + 1);
      }

      throw new Error(
        `API request failed after ${maxRetries} attempts: ${
          errorData.error?.message || "Unknown error"
        }`
      );
    }

    const data = await response.json();

    if (
      !data.candidates ||
      !data.candidates[0] ||
      !data.candidates[0].content ||
      !data.candidates[0].content.parts
    ) {
      throw new Error("Invalid API response format");
    }

    return data.candidates[0].content.parts[0].text;
  } catch (error) {
    console.error("Error getting Gemini answer:", error);

    // Fallback response if everything fails
    return "I'm sorry, I couldn't process your request at the moment. Please try asking a different question about the Financial Literacy Program, or check our FAQ section for common questions.";
  }
}

// Helper function to check if API is working
export async function verifyGeminiApiKey() {
  try {
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    console.log(
      "Verifying API key (first 4 chars):",
      apiKey ? apiKey.substring(0, 4) + "..." : "No key found"
    );

    if (!apiKey) {
      return {
        status: "invalid",
        message: "API Key is not set in environment variables",
      };
    }

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1/models?key=${apiKey}`
    );

    console.log("API response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log(
        "API models available:",
        data.models ? data.models.length : 0
      );
      return { status: "valid", message: "API connection successful" };
    } else {
      const errorData = await response.json();
      console.error(
        "API Key Error details:",
        JSON.stringify(errorData, null, 2)
      );
      return {
        status: "invalid",
        message: `API Key error: ${
          errorData.error?.message || "Unknown error"
        }`,
      };
    }
  } catch (error) {
    console.error("API Verification Error:", error);
    return {
      status: "error",
      message: `Failed to connect to API: ${error.message}`,
    };
  }
}

// Function to process file contents
export async function processAttachment(file) {
  try {
    if (!file) return null;

    // Read file content
    const content = await readFileContent(file);

    // Get analysis from Gemini
    if (content) {
      const analysisPrompt = `
      This is a file from a user related to the Financial Literacy Program. 
      The file is named "${file.name}" and the content appears to be:
      
      ${content.substring(0, 3000)} ${
        content.length > 3000 ? "...(content truncated)" : ""
      }
      
      Please provide a brief summary of what this file contains and how it might relate to financial literacy education.
      `;

      const analysis = await getGeminiAnswer(analysisPrompt);
      return {
        fileName: file.name,
        fileType: file.type,
        fileSize: formatFileSize(file.size),
        analysis,
      };
    }

    return null;
  } catch (error) {
    console.error("Error processing attachment:", error);
    return {
      fileName: file.name,
      fileType: file.type,
      fileSize: formatFileSize(file.size),
      analysis: "Could not analyze this file type.",
    };
  }
}

// Helper function to read file content
function readFileContent(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject("No file provided");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    // Handle different file types
    if (
      file.type.match("text.*") ||
      file.type === "application/json" ||
      file.type === "application/pdf" ||
      file.type.includes("document")
    ) {
      reader.readAsText(file);
    } else if (file.type.match("image.*")) {
      // For images, we'll just acknowledge we received an image
      resolve(`[Image file: ${file.name}]`);
    } else {
      // For unknown types
      resolve(`[File: ${file.name} (${file.type})]`);
    }
  });
}

// Helper function to format file size
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
}
